import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { ProgramCard, ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { AICitations } from "@/components/AICitations";
import { SERVICES } from "@/content/services";
import { SITE } from "@/content/site";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { RangpurAreasStrip } from "@/components/RangpurAreasStrip";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";

const SERVICE = SERVICES.find((s) => s.slug === "aeo-geo")!;

export const Route = createFileRoute("/services/aeo-geo")({
  head: () =>
    buildSeo({
      title: "AEO & GEO Services — Get Cited by ChatGPT, Perplexity & Gemini | Freelancer Tamal",
      description:
        "Answer Engine & Generative Engine Optimization. Earn AI citations in ChatGPT, Perplexity, Gemini and Google AI Overviews with entity SEO, schema, and citation-worthy content.",
      path: "/services/aeo-geo",
    ogEyebrow: "AEO & GEO",
      image: "/og/aeo-geo.jpg",
      keywords:
        "AEO services, GEO services, answer engine optimization, generative engine optimization, ChatGPT SEO, Perplexity SEO, AI search optimization",
    }),
  component: AeoGeoPage,
});

function AeoGeoPage() {
  const s = SERVICE;
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "AEO & GEO", path: "/services/aeo-geo" }]} />
      <Section headingLevel={1}
        eyebrow="AEO & GEO"
        title={
          <>
            Get cited by <span className="text-gradient">ChatGPT, Perplexity & Gemini.</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">{s.description}</p>
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "AEO (Answer Engine Optimization) gets your brand cited inside ChatGPT, Perplexity, Gemini, and Google AI Overviews — not just ranked on the blue links.",
              "It works by stacking entity authority, citation-worthy content structure, and the right schema (FAQPage, Article, Organization with sameAs) so LLMs trust and quote you.",
              "Brands that ignore AEO will keep ranking but lose share of voice in AI answers — where more and more questions are answered before anyone clicks a link.",
            ]}
          />
          <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />
          <p className="mt-6 text-muted-foreground">
            Need just one? See dedicated{" "}
            <Link to="/services/aeo" className="text-primary underline underline-offset-4">AEO services</Link>{" "}
            (ChatGPT, Perplexity, Claude) or{" "}
            <Link to="/services/geo" className="text-primary underline underline-offset-4">GEO services</Link>{" "}
            (Google AI Overviews &amp; AI Mode), with prices on the{" "}
            <Link to="/pricing" className="text-primary underline underline-offset-4">pricing page</Link>.
          </p>
        </div>
      </Section>

      <ProgramCard>
        <ProgramIntro label="Service" title={s.title} tagline={s.tagline}>
          <div className="mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
              Start an AEO engagement <ArrowUpRight size={16} />
            </Link>
          </div>
        </ProgramIntro>
        <ProgramDetails deliverables={s.deliverables} outcomes={s.outcomes}>
          <ProgramRow heading="Live proof — recent AI citations" spaced>
            <AICitations />
          </ProgramRow>
          <ProgramRow heading="People also ask" spaced>
            <FaqAccordion items={s.faqs} idPrefix="faq-aeo-geo" />
          </ProgramRow>
        </ProgramDetails>
      </ProgramCard>

      <Section title="Explore the rest of the SEO program.">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary transition"
        >
          See all services <ArrowUpRight size={16} />
        </Link>
        <AiAnswersCallout
          context="AEO, GEO & AI-search visibility"
          questionSlugs={[
            "aeo-vs-seo-vs-geo",
            "geo-what-is",
            "get-cited-by-chatgpt",
            "measure-aeo-citations",
          ]}
        />
      </Section>

      <RangpurAreasStrip
        eyebrow="AEO/GEO · Rangpur"
        title={<>AI search visibility, <span className="text-gradient">block by block.</span></>}
        description="AEO and GEO compound when paired with strong hyperlocal entity signals. See how the AI-search playbook adapts for each Rangpur neighborhood and intersection."
      />

      <RelatedReading
        eyebrow="Deep-dive guides"
        title={<>Everything I know about <span className="text-gradient">AI search visibility.</span></>}
        hrefs={[
          "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026",
          "/blog/schema-markup-for-aeo-2026",
          "/blog/llms-txt-explained-2026",
          "/blog/entity-seo-signals-for-aeo",
          "/blog/measure-aeo-performance-2026",
          "/blog/cite-framework-aeo",
          "/blog/anatomy-of-chatgpt-cited-paragraph",
          "/blog/how-llms-choose-citations-2026",
          "/blog/aeo-vs-geo-vs-seo-vs-llmo-2026",
        ]}
      />

      <RelatedReading
        eyebrow="Pricing & ROI"
        title={<>Budget and timeline for <span className="text-gradient">AI-search work.</span></>}
        description="What AEO, GEO, and ongoing SEO retainers cost in Bangladesh, and how long results realistically take."
        hrefs={[
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
          "/blog/how-long-does-seo-take-bangladesh-2026",
        ]}
      />

      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: s.title,
          serviceType: "Answer Engine Optimization",
          description: s.description,
          areaServed: ["Bangladesh", "Worldwide"],
          provider: {
            "@type": "Person",
            "@id": `${SITE.url}/#person`,
            name: SITE.fullName,
            url: `${SITE.url}/about`,
            jobTitle: "SEO Expert & Digital Marketing Consultant",
            sameAs: Object.values(SITE.social),
          },
          review: {
            "@type": "Review",
            reviewBody:
              "AEO & GEO service page written, fact-checked, and maintained by Freelancer Tamal — author of the AEO Implementation Checklist with a focus on technical SEO and AI-search readiness.",
            author: {
              "@type": "Person",
              "@id": `${SITE.url}/#person`,
              name: SITE.fullName,
              url: `${SITE.url}/about`,
              jobTitle: "SEO Expert",
            },
            datePublished: new Date().toISOString().slice(0, 10),
          },
        }}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: s.title, path: "/services/aeo-geo" },
        ])}
      />
    </>
  );
}